import React, { useEffect, useMemo, useRef, useState } from 'react';
import { formatFilterOptionLabel } from './filterHelpers';

export interface FilterConfig<T> {
  key: string;
  title: string;
  getOptions: (data: T[]) => string[];
  getValueFromItem: (item: T) => string[];
}

interface FilterDropdownProps<T> {
  title: string;
  data: T[];
  getOptions: (data: T[]) => string[];
  selectedFilters: string[];
  setSelectedFilters: (filters: string[]) => void;
  formatLabel?: (option: string) => string;
}

export const FilterDropdown = <T extends object>({
  title,
  data,
  getOptions,
  selectedFilters,
  setSelectedFilters,
  formatLabel = formatFilterOptionLabel,
}: FilterDropdownProps<T>) => {
  const [open, setOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState<string[]>(selectedFilters);
  const [searchQuery, setSearchQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTempFilters(selectedFilters);
  }, [selectedFilters]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const allOptions = useMemo(() => getOptions(data ?? []), [data, getOptions]);

  const filteredOptions = allOptions.filter((option) =>
    formatLabel(option).toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const toggleOption = (option: string) => {
    setTempFilters((prev) =>
      prev.includes(option)
        ? prev.filter((s) => s !== option)
        : [...prev, option],
    );
  };

  const selectAll = () => setTempFilters([...allOptions]);
  const clearAll = () => setTempFilters([]);
  const applyFilter = () => {
    setSelectedFilters(tempFilters);
    setSearchQuery('');
    setOpen(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) setSearchQuery('');
    setOpen(nextOpen);
  };

  return (
    <div ref={ref} className="relative mr-2 inline-block">
      <button
        className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-xs hover:bg-gray-50 focus:outline-hidden"
        onClick={() => handleOpenChange(!open)}
        type="button"
      >
        {title}
        <svg
          className="ml-2 h-4 w-4"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {open && (
        <div className="absolute z-10 mt-2 w-56 rounded-lg border border-gray-200 bg-white p-4 shadow-lg">
          <div className="relative mb-2">
            <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <svg
                className="h-4 w-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </span>
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              className="block w-full rounded-md border border-gray-300 py-2 pr-3 pl-9 text-sm placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
            />
          </div>
          <div className="mb-2 flex items-center justify-between">
            <label className="flex cursor-pointer items-center text-sm">
              <input
                type="checkbox"
                checked={tempFilters.length === allOptions.length}
                onChange={(e) => (e.target.checked ? selectAll() : clearAll())}
                className="mr-2"
              />
              Select All
            </label>
            <button
              className="text-xs text-blue-600 hover:underline"
              onClick={clearAll}
              type="button"
            >
              Clear filter
            </button>
          </div>
          <div className="mb-2 max-h-40 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <p className="py-2 text-sm text-gray-500">No matches found</p>
            ) : (
              filteredOptions.map((option) => (
                <label
                  key={option}
                  className="mb-1 flex cursor-pointer items-center text-sm"
                >
                  <input
                    type="checkbox"
                    checked={tempFilters.includes(option)}
                    onChange={() => toggleOption(option)}
                    className="mr-2"
                  />
                  {formatLabel(option)}
                </label>
              ))
            )}
          </div>
          <button
            className="w-full rounded-sm bg-blue-500 py-2 text-sm font-semibold text-white hover:bg-blue-600"
            onClick={applyFilter}
            type="button"
          >
            Apply Filter
          </button>
        </div>
      )}
    </div>
  );
};
